import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { CoreRead } from '@exylon/generated-contract-kernel/types';
import { IdentityHttpError } from '../../runtime/identity-http';
import type { PanelGeneratedHttpPort, PanelLiveScreenId, PanelScreenConnection } from '../runtime-port';
import { FrozenInteractionBoundary, LoadingRegion, SystemBanner } from '../state';
import { parseParteBridge, parteBridgeDerivedId, parteBridgeScreenRow, stripParteBridge, type ParteBridgeRequest } from './contract';
import { parteBridgeSeedLines } from './labels';
import './parte-bridge.scss';

export type ParteBridgeSeedValue = string | number | boolean | readonly string[] | readonly number[];
type ParteBridgeBaseRef = Readonly<{ kind: string; id: string; version: number | null; state: string | null }>;

/** Why the screen does not open the seeded form («Puente · rechazo»). */
type ParteBridgeRefusalReason =
  | 'permission_revoked' | 'base_changed' | 'not_found' | 'already_applied' | 'invalid_parameters'
  | 'invalid_link' | 'restaurant_mismatch';

/** What a destination screen receives once the owner has revalidated the link. */
export interface ParteBridgeHandoff {
  readonly token: string;
  readonly form: string;
  readonly verb: string;
  /** The owner's revalidated seeds: the screen seeds its form from these, never from the URL. */
  readonly seed: Readonly<Record<string, ParteBridgeSeedValue>>;
  readonly base: readonly ParteBridgeBaseRef[];
  /** The id of what the form creates: the same for every tab that opens this link. */
  readonly derivedId: string;
  /** The idempotency key of the form's command: the same for every tab that opens this link. */
  readonly idempotencyKey: string;
  /** Drops the link from the address once the form is confirmed or closed. */
  readonly consume: () => void;
}

type BridgeState =
  | Readonly<{ kind: 'none' }>
  | Readonly<{ kind: 'waiting'; request: ParteBridgeRequest }>
  | Readonly<{ kind: 'checking'; request: ParteBridgeRequest }>
  /** `revalidation`: the owner is asked again when the session comes back; the open form stays meanwhile. */
  | Readonly<{ kind: 'open'; request: ParteBridgeRequest; seed: ParteBridgeHandoff['seed']; base: ParteBridgeHandoff['base'];
    revalidation: 'fresh' | 'checking' | 'failed' }>
  | Readonly<{ kind: 'refused'; reason: ParteBridgeRefusalReason; request: ParteBridgeRequest | null }>
  | Readonly<{ kind: 'unavailable'; request: ParteBridgeRequest }>;

type BridgeAnswer = Readonly<{
  contract: string;
  form: string;
  verdict: 'open' | 'refused';
  reason: 'base_changed' | 'not_found' | 'already_applied' | 'invalid_parameters' | null;
  base: readonly ParteBridgeBaseRef[];
  seed: Readonly<Record<string, ParteBridgeSeedValue>>;
}>;

const isAnswer = (value: unknown, form: string): value is BridgeAnswer => {
  if (typeof value !== 'object' || value === null) return false;
  const answer = value as Partial<BridgeAnswer>;
  return typeof answer.contract === 'string' && /\.bridge-revalidation@1\.0$/.test(answer.contract) && answer.form === form
    && (answer.verdict === 'open' || answer.verdict === 'refused') && Array.isArray(answer.base)
    && typeof answer.seed === 'object' && answer.seed !== null;
};

type BridgeAnswerState = Extract<BridgeState, { kind: 'open' | 'refused' | 'unavailable' }>;

const sameValue = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);

/**
 * The owner's answer after a link was already open. A transient failure keeps the open form (frozen, with a
 * retry) so what the person edited survives; a definitive refusal replaces it as on opening. An unchanged
 * answer keeps the seed and base it had, so the handoff keeps its identity.
 */
function settleRevalidation(previous: BridgeState, answer: BridgeAnswerState): BridgeState {
  if (previous.kind !== 'open' || previous.request.token !== answer.request?.token || previous.request.form !== answer.request.form) return answer;
  if (answer.kind === 'unavailable') return { ...previous, revalidation: 'failed' };
  if (answer.kind !== 'open') return answer;
  return { ...previous, revalidation: 'fresh',
    seed: sameValue(previous.seed, answer.seed) ? previous.seed : answer.seed,
    base: sameValue(previous.base, answer.base) ? previous.base : answer.base };
}

/** The owner's revalidation of one link, run on opening and again whenever the session comes back. */
async function revalidateParteBridge(
  client: Pick<PanelGeneratedHttpPort, 'read'>,
  request: ParteBridgeRequest,
): Promise<BridgeAnswerState> {
  const command = { operation: request.operation, params: { restaurantId: request.restaurantId }, query: { form: request.form, ...request.parameters } } as unknown as CoreRead;
  try {
    const response = await client.read(command) as { readonly result?: unknown };
    const answer = response.result;
    if (!isAnswer(answer, request.form)) return { kind: 'unavailable', request };
    if (answer.verdict === 'refused') return { kind: 'refused', reason: answer.reason ?? 'invalid_parameters', request };
    return { kind: 'open', request, seed: answer.seed, base: answer.base, revalidation: 'fresh' };
  } catch (error) {
    if (error instanceof IdentityHttpError) {
      if (error.kind === 'permission' || error.status === 403) return { kind: 'refused', reason: 'permission_revoked', request };
      if (error.status === 400 || error.kind === 'invalid_request') return { kind: 'refused', reason: 'invalid_parameters', request };
    }
    return { kind: 'unavailable', request };
  }
}

export interface ParteBridgeGateProps {
  readonly screen: PanelLiveScreenId;
  readonly client: Pick<PanelGeneratedHttpPort, 'read'>;
  readonly restaurantId: string;
  readonly search: string;
  readonly sessionActive: boolean;
  readonly connection: PanelScreenConnection;
  readonly onSearchChange: (search: string) => void;
  readonly children: (handoff: ParteBridgeHandoff | null) => ReactNode;
}

const initial = (parse: ReturnType<typeof parseParteBridge>): BridgeState =>
  parse.kind === 'none' ? { kind: 'none' } : parse.kind === 'invalid' ? { kind: 'refused', reason: parse.reason, request: null } : { kind: 'waiting', request: parse.request };

/**
 * The bridge in front of a destination screen. Without `parte` in the address it renders the screen untouched.
 * With a link it asks the owner first: an open verdict hands the screen the revalidated seeds under the
 * «Vienes de El Parte» band; a refusal shows «Puente · rechazo» with the reason and never opens the form.
 */
export function ParteBridgeGate(props: ParteBridgeGateProps) {
  const { screen, client, restaurantId, search, sessionActive, connection, onSearchChange, children } = props;
  const parse = useMemo(() => parseParteBridge(search, screen, restaurantId), [search, screen, restaurantId]);
  const [state, setState] = useState<BridgeState>(() => initial(parse));
  const [attempt, setAttempt] = useState(0);
  const consume = useCallback(() => onSearchChange(stripParteBridge(search)), [onSearchChange, search]);

  const ready = sessionActive && connection === 'online';
  useEffect(() => {
    const next = initial(parse);
    const sameLink = (value: BridgeState) => next.kind === 'waiting' && 'request' in value && value.request?.token === next.request.token
      && value.request.form === next.request.form;
    // Session interruption: nothing is read (or seeded) until the session is back; a form already open stays
    // mounted meanwhile (the screen freezes it), and the owner is asked again when the session returns.
    // No link before and none now: the same state, so a connection change does not re-render the screen.
    setState((previous) => sameLink(previous) && previous.kind === 'open' || previous.kind === 'none' && next.kind === 'none' ? previous : next);
    if (next.kind !== 'waiting' || !ready) return undefined;
    let current = true;
    setState((previous) => sameLink(previous) && previous.kind === 'open' ? { ...previous, revalidation: 'checking' } : { kind: 'checking', request: next.request });
    void revalidateParteBridge(client, next.request).then((answer) => { if (current) setState((previous) => settleRevalidation(previous, answer)); });
    return () => { current = false; };
  }, [parse, client, ready, attempt]);

  // The handoff keeps its identity while the link is revalidated, so a destination's memos and effects do not re-run.
  const openRequest = state.kind === 'open' ? state.request : null;
  const openSeed = state.kind === 'open' ? state.seed : null;
  const openBase = state.kind === 'open' ? state.base : null;
  const handoff = useMemo<ParteBridgeHandoff | null>(() => openRequest === null || openSeed === null || openBase === null ? null : {
    token: openRequest.token, form: openRequest.form, verb: openRequest.verb, seed: openSeed, base: openBase,
    derivedId: parteBridgeDerivedId(openRequest), idempotencyKey: parteBridgeDerivedId(openRequest, 'idempotency'), consume,
  }, [openRequest, openSeed, openBase, consume]);

  const label = parteBridgeScreenRow(screen)?.label ?? 'esta pantalla';
  // Once a link has opened the form, the screen keeps the freeze wrapper after the link is dropped.
  const opened = useRef(false);
  if (state.kind === 'open') opened.current = true;
  // One tree shape with and without the band, so dropping the link never remounts the screen.
  if (state.kind === 'none') {
    return <>{null}{opened.current ? <FrozenInteractionBoundary frozen={false} showBanner={false}>{children(null)}</FrozenInteractionBoundary> : children(null)}</>;
  }
  if (state.kind === 'waiting' || state.kind === 'checking') {
    return <section className="parte-bridge" data-parte-bridge="checking"><LoadingRegion label="Comprobando el enlace de El Parte con los datos de ahora" rows={2} /></section>;
  }
  if (state.kind === 'refused') return <ParteBridgeRefusal reason={state.reason} screenLabel={label} onDismiss={consume} />;
  if (state.kind === 'unavailable') return <ParteBridgeRefusal reason="unavailable" screenLabel={label} onDismiss={consume} onRetry={() => setAttempt((value) => value + 1)} />;
  return <>
    <>
      <ParteBridgeBand handoff={handoff!} />
      <ParteBridgeRevalidation state={state.revalidation} onRetry={() => setAttempt((value) => value + 1)} />
    </>
    <FrozenInteractionBoundary frozen={state.revalidation !== 'fresh'} showBanner={false}>{children(handoff)}</FrozenInteractionBoundary>
  </>;
}

const REFUSAL_COPY: Readonly<Record<ParteBridgeRefusalReason | 'unavailable', Readonly<{ title: string; body: string }>>> = Object.freeze({
  permission_revoked: { title: 'Ya no tienes permiso para esto', body: 'Tu permiso para esta acción ha cambiado desde que se escribió el parte. No se ha abierto el formulario; pídeselo a quien gestiona los permisos si lo necesitas.' },
  base_changed: { title: 'Los datos han cambiado', body: 'Lo que el parte leyó ya no es lo que hay ahora (alguien lo cambió después). No se ha abierto el formulario para no rellenarlo con datos viejos.' },
  not_found: { title: 'Ya no existe', body: 'Lo que proponía el parte ya no está: se borró o se retiró. No se ha abierto el formulario.' },
  already_applied: { title: 'Ya está hecho', body: 'Esto ya se hizo, en esta pestaña, en otra o a mano. No se ha abierto el formulario para no hacerlo dos veces.' },
  invalid_parameters: { title: 'El enlace no encaja', body: 'Los datos del enlace no casan con lo que hay en este restaurante. No se ha abierto el formulario.' },
  invalid_link: { title: 'El enlace no es válido', body: 'Este enlace no tiene la forma de un enlace de El Parte. No se ha abierto nada.' },
  restaurant_mismatch: { title: 'El enlace es de otro restaurante', body: 'Estás en otro restaurante que el del parte. No se ha abierto el formulario.' },
  unavailable: { title: 'No se ha podido comprobar el enlace', body: 'No hemos podido confirmar los datos con el servidor. No se ha abierto el formulario; puedes reintentarlo.' },
});

/** «Puente · rechazo»: the reason, and the way back to the screen without the link. */
function ParteBridgeRefusal({ reason, screenLabel, onDismiss, onRetry }: {
  readonly reason: ParteBridgeRefusalReason | 'unavailable';
  readonly screenLabel: string;
  readonly onDismiss: () => void;
  readonly onRetry?: () => void;
}) {
  const copy = REFUSAL_COPY[reason];
  return <section className="parte-bridge parte-bridge--refusal" data-parte-bridge="refused" data-parte-bridge-reason={reason} aria-labelledby="parte-bridge-refusal-title">
    <p className="parte-bridge__kicker">Vienes de El Parte</p>
    <h1 className="parte-bridge__title" id="parte-bridge-refusal-title">{copy.title}</h1>
    <p className="parte-bridge__body" role="alert">{copy.body}</p>
    <div className="parte-bridge__actions">
      {onRetry ? <button className="btn btn--primary" onClick={onRetry} type="button">Reintentar</button> : null}
      <button className={onRetry ? 'btn btn--ghost' : 'btn btn--primary'} onClick={onDismiss} type="button">Ir a {screenLabel} sin el enlace</button>
    </div>
  </section>;
}

/** The open form while the owner is asked again: it stays, frozen, and a failed check offers a retry. */
function ParteBridgeRevalidation({ state, onRetry }: { readonly state: 'fresh' | 'checking' | 'failed'; readonly onRetry: () => void }) {
  if (state === 'fresh') return null;
  if (state === 'checking') {
    return <div className="parte-bridge-revalidation" data-parte-bridge-revalidation="checking"><SystemBanner>Volviendo a comprobar el enlace…</SystemBanner></div>;
  }
  return <div className="parte-bridge-revalidation" data-parte-bridge-revalidation="failed">
    <SystemBanner tone="caution" urgent>No se ha podido volver a comprobar el enlace. Tu formulario sigue aquí.</SystemBanner>
    <button className="btn btn--secondary" onClick={onRetry} type="button">Reintentar</button>
  </div>;
}

/** «Vienes de El Parte»: the verb, the seeded fields and the way to drop the link. */
function ParteBridgeBand({ handoff }: { readonly handoff: ParteBridgeHandoff }) {
  const lines = parteBridgeSeedLines(handoff.seed);
  return <aside className="parte-bridge parte-bridge--band" data-parte-bridge="open" data-parte-bridge-token={handoff.token} aria-label="Vienes de El Parte">
    <div className="parte-bridge__band-text">
      <p className="parte-bridge__kicker">Vienes de El Parte</p>
      <p className="parte-bridge__verb">{handoff.verb}. Nada se guarda hasta que lo confirmes aquí.</p>
      {lines.length > 0 ? <ul className="parte-bridge__seeds" aria-label="Campos rellenados por el parte">
        {lines.map((line) => <li key={line.key}><span>{line.label}</span> <strong>{line.value}</strong></li>)}
      </ul> : null}
    </div>
    <button className="btn btn--ghost" onClick={handoff.consume} type="button">Quitar el enlace</button>
  </aside>;
}
